import React, { useEffect, useState } from "react";
import Logo from "../Assets/images/Logo-1.png";
import { generateOtp, saveFcmToken, verifyOtp } from "../api/loginUser";
import toast from "react-hot-toast";
import { getFromStorage, saveInStorage } from "../lib/storage";
import { useNavigate } from "react-router";
import OtpVerifyPage from "./OtpPage";
import PersonalInfoPage from "./PersonalInfoPage";
import { messaging, isSupported } from "../utils/firebase";
import { getToken } from "firebase/messaging";
import { addToCart } from "../api/cartService";
import { enrollNow } from "../api/homeService";

const LoginPage = ({
  isLoginModalOpen,
  setIsLoginModalOpen,
  step,
  setStep,
  course,
  isEnroll,
  errorCode,
  setisEnroll,
  setErrCode,
}) => {
  const [mobileOrEmail, setMobileOrEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [profilePageInfo, setProfilePageInfo] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [fcmToken, setFcmToken] = useState("");
  const [error, setError] = useState("");
  const [loginMode, setLoginMode] = useState("mobile");
  const navigate = useNavigate();
  const handleEnrollNow = async () => {
    const courseUuid = course?.courseUuid || course?.uuid;
    if (!courseUuid) {
      console.error("No course UUID available for enrollment");
      return;
    }
    try {
      const response = await enrollNow(courseUuid);
      if (response?.data?.errorCode === 0) {
        navigate(`/Dashboard`);
        setTimeout(() => {
          toast.success(response?.data?.message);
        }, 2000);
        window.dispatchEvent(new Event("notificationsUpdated"));
      } else {
        toast.error(response?.data?.message);
        navigate("/Dashboard");
        setErrCode(response?.data?.errorCode);
      }
    } catch (error) {
      console.error("Server error while enrolling");
    }
  };

  const fetchFcmToken = async () => {
    try {
      const supported = await isSupported();
      if (!supported || !messaging) {
        console.warn("🚫 FCM not supported in this browser");
        return null;
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.warn("❌ Notifications permission not granted");
        return null;
      }

      const currentToken = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
      });

      if (currentToken) {
        setFcmToken(currentToken);
        return currentToken;
      }
      return null;
    } catch (err) {
      console.error("🔥 Error fetching FCM token:", err);
      return null;
    }
  };

  const validateInput = () => {
    if (!mobileOrEmail.trim()) {
      return loginMode === "mobile" ? "Mobile number is required" : "Email is required";
    }

    if (loginMode === "mobile") {
      const mobileRegex = /^[6-9]\d{9}$/;
      if (!mobileRegex.test(mobileOrEmail)) {
        return "Enter a valid 10-digit mobile number starting with 6-9";
      }
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(mobileOrEmail)) {
        return "Enter a valid email address";
      }
    }

    return "";
  };

  const handleContinue = async (e) => {
    e.preventDefault();

    const validationError = validateInput();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setLoading(true);
    try {
      const response = await generateOtp(mobileOrEmail);
      if (response?.errorCode === 0) {
        toast.success(response?.message || "OTP Sent Successfully");
        setStep("otp");
      } else {
        toast.error(response.message);
        setError(response?.responsePacket[0] || "Failed To Send OTP");
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (enteredOtp) => {
    if (!enteredOtp || enteredOtp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await verifyOtp(mobileOrEmail, enteredOtp);
      if (res?.errorCode === 0) {
        saveInStorage("lmsCCUser", JSON.stringify(res));
        saveInStorage("lmsCCToken", res.responsePacket.secretKey);
        toast.success(res?.message || "OTP Verified Successfully");
        const token = await fetchFcmToken();
        if (token) {
          await saveFcmToken(token);
        }
        const localCart =
          JSON.parse(localStorage.getItem("lmsCartItems")) || [];

        if (!res.responsePacket.firstName) {
          localStorage.setItem("personalInfoCompleted", "false");
          setIsLoginModalOpen(false);
          navigate("/MyProfilePage", { state: { isNewUser: true } });
        } else {
          localStorage.setItem("personalInfoCompleted", "true");
          setIsLoginModalOpen(false);

          // Sync cart if any
          if (localCart.length > 0) {
            for (const item of localCart) {
              try {
                await addToCart(item);
              } catch (err) {
                console.error("Failed to sync cart item:", err);
              }
            }
            localStorage.removeItem("lmsCartItems");
            window.dispatchEvent(new Event("cartUpdated"));
          }

          // Enroll only if isEnroll is true
          if (isEnroll) {
            await handleEnrollNow();
            return; // ✅ stop further navigation
          }

          // Navigate based on cart
          if (localCart.length > 0) navigate("/CartPage");
          else navigate("/");
        }

        localStorage.setItem(
          "profilePageInfo",
          JSON.stringify(res.responsePacket)
        );
        setProfilePageInfo(res?.responsePacket);
      } else {
        toast.error(res?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error("OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const response = await generateOtp(mobileOrEmail);
      if (response?.errorCode === 0) {
        toast.success("OTP Resent Successfully");
      } else {
        toast.error("Failed To Resend OTP");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  if (step === "otp") {
    return (
      <OtpVerifyPage
        mobileOrEmail={mobileOrEmail}
        onOtpSubmit={handleVerifyOtp}
        onResendOtp={handleResendOtp}
        loading={loading}
        isLoginModalOpen={isLoginModalOpen}
      />
    );
  }

  if (step === "personal") {
    return (
      <PersonalInfoPage
        course={course}
        isEnroll={isEnroll}
        profilePageInfo={profilePageInfo}
        isLoginModalOpen={isLoginModalOpen}
        setIsLoginModalOpen={setIsLoginModalOpen}
      />
    );
  }

  return (
    <div
      className={`${
        isLoginModalOpen ? "" : "min-h-screen"
      } flex items-center justify-center bg-gray-100 rounded-xl`}
    >
      <form
        onSubmit={handleContinue}
        className="bg-white shadow-md rounded-lg p-6 w-full max-w-md border py-6"
      >
        <div className="flex justify-center my-5">
          {/* <img src={Logo} alt="Logo" className="h-12" /> */}
        </div>

        <div className="mb-5 text-center">
          <h3 className="text-xl font-semibold">Login</h3>
          <p>
            {loginMode === "mobile"
              ? "Enter your mobile number to access your account"
              : "Enter your verified email to access your account"}
          </p>
        </div>

        <div className="mb-5">
          {loginMode === "mobile" ? (
            <>
              <label
                htmlFor="mobileNumber"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                value={mobileOrEmail}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setMobileOrEmail(val);
                  if (val.trim() === "") {
                    setError("");
                  }
                }}
                placeholder="Enter your 10-digit mobile number"
                className={`w-full text-sm rounded-lg p-2.5 border ${
                  error
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
                maxLength={10}
              />
            </>
          ) : (
            <>
              <label
                htmlFor="emailAddress"
                className="block mb-2 text-sm font-medium text-gray-900"
              >
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="emailAddress"
                value={mobileOrEmail}
                onChange={(e) => {
                  setMobileOrEmail(e.target.value);
                  if (e.target.value.trim() === "") {
                    setError("");
                  }
                }}
                placeholder="Enter your verified email address"
                className={`w-full text-sm rounded-lg p-2.5 border ${
                  error
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-300 focus:border-blue-500"
                }`}
              />
            </>
          )}

          {error && <p className="text-red-500 text-m mt-1">{error}</p>}
          <p className="note pt-2 text-sm text-gray-500">
            We will send a verification code to confirm it’s you.
          </p>
        </div>

        <div className="mb-3 text-center">
          <button
            type="button"
            onClick={() => {
              setLoginMode(loginMode === "mobile" ? "email" : "mobile");
              setMobileOrEmail("");
              setError("");
            }}
            className="text-xs text-gray-500 hover:text-gray-800 hover:underline"
          >
            {loginMode === "mobile"
              ? "Login via verified email"
              : "Login via mobile number"}
          </button>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="block w-full p-[6px] bg-[var(--primary-button)] text-white rounded-md flex justify-center items-center hover:opacity-90 disabled:opacity-60"
        >
          {loading && (
            <div className="border-t-transparent border-white border-2 rounded-full w-5 h-5 animate-spin mr-2"></div>
          )}
          Continue
        </button>

        <p className="text-xs text-center text-gray-400 mt-3">
          Students outside India, please contact support.
        </p>
      </form>
    </div>
  );
};

export default LoginPage;
